import {
  ContextualMenu,
  DirectionalHint,
  IContextualMenuItem,
  Label,
} from "@fluentui/react";
import { FieldContainer } from "components/controls/FieldContainer";
import { useField, useFormikContext } from "formik";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { VSpace } from "../Spacer";
import {
  ErrorMessage,
  Flex,
  StyledIconButton,
  StyledTextField,
} from "../styled";
import { TextWithOptionsFieldConfig } from "./types";
import { useDynamicConfig } from "./useDynamicConfig";

type Props = { fieldConfig: TextWithOptionsFieldConfig };
const translations = {
  "bfm.reasonForConcludingInsurance.label": "A biztosítás megkötésének oka",
  "bfm.reasonForCancellingInsurance.label": "A biztosítás lemondásának oka",
  "bfm.reasonForConcludingInsurance.options.inheritanceLegallyBinding":
    "Öröklés jogerős hagyatéki alapján",
  "bfm.reasonForConcludingInsurance.options.contractTerminationAnniversary":
    "Biztosítási szerződés évfordulóra történő felmondása",
  "bfm.reasonForConcludingInsurance.options.newVehicleHungary":
    "Magyarországon vásárolt új, 0 km-es gépjármű tulajdonának megszerzése",
  "bfm.reasonForConcludingInsurance.options.usedVehicleHungary":
    "Magyarországon vásárolt használt gépjármű tulajdonának megszerzése",
  "bfm.reasonForConcludingInsurance.options.importedVehicleFirstRegistration":
    "Külföldről behozott és Magyarországon először forgalomba helyezett gépjármű tulajdonának megszerzése",
  "bfm.reasonForConcludingInsurance.options.operatorChange":
    "Üzembentartó-váltás (új üzembentartó bejegyzése)",
  "bfm.reasonForConcludingInsurance.options.contractRenewalAfterNonPayment":
    "Díjnemfizetés miatt megszűnt szerződés újrakötése",
  "bfm.reasonForConcludingInsurance.options.vehicleReinstatementToTraffic":
    "Forgalomból kivont gépjármű forgalomba történő visszahelyezése",
  "bfm.reasonForConcludingInsurance.options.contractRenewalMutualAgreement":
    "Közös megegyezéssel megszűnt szerződés újrakötése",
  "bfm.reasonForConcludingInsurance.options.insuranceTransfer":
    "Biztosítás átkötése",
  "bfm.reasonForConcludingInsurance.options.other": "Egyéb",

  "bfm.reasonForCancellingInsurance.options.noHistory": "Nincs előzmény",
  "bfm.reasonForCancellingInsurance.options.nonPayment": "DNF",
  "bfm.reasonForCancellingInsurance.options.saleExpiration": "Eladás (lejárat)",
  "bfm.reasonForCancellingInsurance.options.loseInterest": "Érdekmúlás",
  "bfm.reasonForCancellingInsurance.options.insuranceCancellationAnniversaryInsurer":
    "Biztosító felm. évfordulóra",
  "bfm.reasonForCancellingInsurance.options.insuranceCancellationAnniversaryClient":
    "Szerződő felm. évfordulóra",
  "bfm.reasonForCancellingInsurance.options.finalWithdrawalFromCirculation":
    "Forgalomból végeleg kivonva",
  "bfm.reasonForCancellingInsurance.options.economicTotalLoss":
    "Gazdasági totálkár",
  "bfm.reasonForCancellingInsurance.options.technicalTotalLoss":
    "Műszaki totálkár",
  "bfm.reasonForCancellingInsurance.options.terminationDueToOperatorChange":
    "Felmondás üzembentartó váltás miatt",
  "bfm.reasonForCancellingInsurance.options.loseInterestDueToOperatorChange":
    "Érdekmúlás üzembentartó váltás miatt",
  "bfm.reasonForCancellingInsurance.options.terminationByMutualAgreement":
    "Megszűnés közös megegyezéssel",
  "bfm.reasonForCancellingInsurance.options.insurerTerminationDueToRisk":
    "Biztosító felmondása kockázati ok miatt",
};

export const TextWithOptionsField = ({ fieldConfig }: Props) => {
  const { values } = useFormikContext();
  const dispatch = useDispatch();
  const config = {
    ...fieldConfig,
    ...useDynamicConfig(fieldConfig.d),
  };
  const { t } = useTranslation();
  const {
    name,
    label,
    isVisible = true,
    isDisabled,
    isResizable,
    isMultiline,
    rows,
    isRequired,
    validate,
    background,
    options = [],
  } = config;
  const [field, { error }, { setValue }] = useField({ name, validate });

  const [showContextMenu, setShowContextMenu] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Convert options to contextual menu items
  const menuItems: IContextualMenuItem[] = useMemo(() => {
    return options.map((option) => ({
      key: option,
      text: translations[option],
      onClick: () => {
        const currentValue = field.value || "";
        const selectedText = translations[option];

        // If field is empty, set the selected text
        // If field has content, append with a separator
        const newValue = currentValue.trim()
          ? `${currentValue}\n${selectedText}`
          : selectedText;

        setValue(newValue);
        setShowContextMenu(false);
      },
    }));
  }, [options, t, field.value, setValue]);

  const handleMenuButtonClick = () => {
    setShowContextMenu(true);
  };

  const handleMenuDismiss = () => {
    setShowContextMenu(false);
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        flex: 1,
      }}
    >
      <FieldContainer
        isTooltipHidden={isDisabled}
        tooltipText={label}
        isReadOnly={true}
      >
        <Label required={isRequired} disabled={isDisabled}>
          {t(label)}
        </Label>

        <Flex
          gap={5}
          direction="row"
          style={{
            width: "100%",
            flex: 1,
            alignItems: "flex-start",
            flexWrap: "nowrap", // Prevent wrapping
          }}
        >
          <StyledTextField
            style={{
              flex: 1,
              minWidth: 0, // Prevents flex item from overflowing
            }}
            styles={{
              root: {
                flex: 1,
                minWidth: 0,
              },
              fieldGroup: {
                width: "100%",
              },
              field: {
                width: "100%",
              },
            }}
            id={name}
            borderless
            disabled={isDisabled}
            multiline={isMultiline}
            resizable={isResizable}
            rows={rows}
            hasError={!!error}
            background={background}
            autoComplete="off"
            {...field}
          />

          {options.length > 0 && (
            <>
              <StyledIconButton
                elementRef={menuButtonRef}
                iconProps={{ iconName: "List" }}
                title={t("bfm.selectFromOptions.title")}
                ariaLabel={t("bfm.selectFromOptions.title")}
                onClick={handleMenuButtonClick}
                disabled={isDisabled}
                style={{
                  marginTop: "2px",
                  minWidth: "32px",
                  width: "32px", // Fixed width
                  height: "32px",
                  flexShrink: 0, // Prevent button from shrinking
                }}
              />

              {showContextMenu && (
                <ContextualMenu
                  items={menuItems}
                  target={menuButtonRef.current}
                  onDismiss={handleMenuDismiss}
                  directionalHint={DirectionalHint.bottomLeftEdge}
                />
              )}
            </>
          )}
        </Flex>

        {!!error && (
          <ErrorMessage id={field.name + "_error"}>{error}</ErrorMessage>
        )}
        <VSpace height={10} />
      </FieldContainer>
    </div>
  );
};
